import { Injectable } from '@angular/core';

import { Selector, StateContext, Action, State } from '@ngxs/store';
import { City, Place, Product } from 'rest-client';

import {
  getPlace,
  getPlaceIdFromProductId,
  getPlaces,
  getProduct,
} from '../utils/booking-state.utils';
import {
  SetBookingInitialDataAction,
  RemoveBookingInitialDataAction,
  SetCitiesAction,
  RemoveCitiesAction,
  SetProductIdAction,
  RemoveProductIdAction,
  SetBookingDatesAction,
  RemoveBookingDatesAction,
  SetBookingDataAction,
  RemoveBookingAction,
} from './booking.actions';
import { BookingStateModel, BookingData, BookingDates } from './booking.model';

const INITIAL_STATE: BookingStateModel = {
  startDate: null,
  endDate: null,
  placeId: null,
  productId: null,
  cities: null,
};

@State<BookingStateModel>({
  name: 'booking',
  defaults: INITIAL_STATE,
})
@Injectable()
export class BookingState {
  @Selector()
  static bookingData(state: BookingStateModel): BookingData {
    const { startDate, endDate, placeId, cities, productId, product } = state;
    let place = null;
    let productReturned: Product;
    if (!placeId) {
      const returnedPlaceId = getPlaceIdFromProductId(cities, productId);
      place = getPlace(cities, returnedPlaceId);
      productReturned = product ? product : getProduct(place, productId);
    } else {
      place = getPlace(cities, placeId);
      productReturned = product ? product : getProduct(place, productId);
    }
    return { startDate, endDate, place, product: productReturned };
  }

  @Selector()
  static bookingDates(state: BookingStateModel): BookingDates {
    const { startDate, endDate } = state;

    return { startDate, endDate };
  }

  @Selector()
  static products(state: BookingStateModel): Product[] {
    const { cities, placeId } = state;

    return getPlace(cities, placeId).products;
  }

  @Selector()
  static places(state: BookingStateModel): Partial<Place>[] {
    const { cities } = state;

    return getPlaces(cities);
  }

  @Selector()
  static cities(state: BookingStateModel): City[] {
    const { cities } = state;

    return cities;
  }

  @Action(SetBookingInitialDataAction)
  setBookingInitialDataAction(
    ctx: StateContext<BookingStateModel>,
    action: SetBookingInitialDataAction
  ): void {
    ctx.patchState({
      startDate: action.startDate,
      endDate: action.endDate,
      placeId: action.placeId,
    });
  }

  @Action(SetBookingDataAction)
  setBookingDataAction(
    ctx: StateContext<BookingStateModel>,
    action: SetBookingDataAction
  ): void {
    ctx.patchState({
      startDate: action.startDate,
      endDate: action.endDate,
      product: action.product,
    });
  }

  @Action(RemoveBookingInitialDataAction)
  removeBookingInitialDataAction(ctx: StateContext<BookingStateModel>): void {
    ctx.patchState({
      startDate: INITIAL_STATE.startDate,
      endDate: INITIAL_STATE.endDate,
      placeId: INITIAL_STATE.placeId,
    });
  }

  @Action(SetCitiesAction)
  setCitiesAction(
    ctx: StateContext<BookingStateModel>,
    action: SetCitiesAction
  ): void {
    ctx.patchState({ cities: action.cities });
  }

  @Action(RemoveCitiesAction)
  removeCitiesAction(ctx: StateContext<BookingStateModel>): void {
    ctx.patchState({ cities: null });
  }

  @Action(SetProductIdAction)
  setProductIdAction(
    ctx: StateContext<BookingStateModel>,
    action: SetProductIdAction
  ): void {
    ctx.patchState({ productId: action.productId });
  }

  @Action(RemoveProductIdAction)
  removeProductIdAction(ctx: StateContext<BookingStateModel>): void {
    ctx.patchState({ productId: null });
  }

  @Action(SetBookingDatesAction)
  setBookingDatesAction(
    ctx: StateContext<BookingStateModel>,
    action: SetBookingDatesAction
  ): void {
    ctx.patchState({ startDate: action.startDate, endDate: action.endDate });
  }

  @Action(RemoveBookingDatesAction)
  removeBookingDatesAction(ctx: StateContext<BookingStateModel>): void {
    ctx.patchState({ startDate: null, endDate: null });
  }

  @Action(RemoveBookingAction)
  removeBookingAction(ctx: StateContext<BookingStateModel>): void {
    ctx.patchState({
      startDate: null,
      endDate: null,
      placeId: null,
      productId: null,
    });
  }
}
